import { Box, Button, IconButton, Paper, Typography } from '@mui/material';
import * as Sentry from '@sentry/react';
import { useCallback, useState } from 'react';

type SentryLog = {
  timestamp: string;
  type: 'error' | 'success' | 'info';
  message: string;
  details?: string;
  eventId?: string;
};

export const SentryDebugPanel = () => {
  const [logs, setLogs] = useState<SentryLog[]>([]);
  const [errorCount, setErrorCount] = useState(0);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const addLog = useCallback(
    (
      type: SentryLog['type'],
      message: string,
      details?: string,
      eventId?: string,
    ) => {
      setLogs(prev => [
        {
          timestamp: new Date().toLocaleTimeString(),
          type,
          message,
          details,
          eventId,
        },
        ...prev.slice(0, 9), // Keep last 10 logs
      ]);
    },
    [],
  );

  const copyToClipboard = useCallback((text: string, eventId: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedId(eventId);
      setTimeout(() => setCopiedId(null), 2000);
    });
  }, []);

  const triggerTestError = useCallback(() => {
    const count = errorCount + 1;
    setErrorCount(count);

    addLog('info', `Triggering test error #${count}...`);

    try {
      const testError = new Error(
        `[SENTRY DEBUG] Test error #${count} - ${new Date().toISOString()}`,
      );

      // Capture with Sentry
      const eventId = Sentry.captureException(testError, {
        tags: {
          debugMode: 'manual-trigger',
          errorNumber: count,
          source: 'debug-panel',
        },
        level: 'error',
        extra: {
          description: 'Manual test error from Sentry Debug Panel',
          timestamp: new Date().toISOString(),
          userAgent: navigator.userAgent,
          hostname: location.hostname,
        },
      });

      if (eventId) {
        addLog(
          'success',
          `Error sent to Sentry successfully`,
          `Event ID: ${eventId}`,
          eventId,
        );
      } else {
        addLog(
          'error',
          'Failed to send to Sentry',
          'No event ID returned - may be blocked',
        );
      }
    } catch (error) {
      addLog(
        'error',
        'Exception while sending to Sentry',
        error instanceof Error ? error.message : String(error),
      );
    }
  }, [errorCount, addLog]);

  const clearLogs = useCallback(() => {
    setLogs([]);
    addLog('info', 'Logs cleared');
  }, [addLog]);

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 16,
        right: 16,
        zIndex: 9999,
        width: 600,
      }}
    >
      <Paper
        elevation={8}
        sx={{
          p: 2,
          backgroundColor: 'rgba(0, 0, 0, 0.9)',
          color: 'white',
          border: '2px solid #ff9800',
          width: '100%',
        }}
      >
        <Typography
          variant="h6"
          sx={{
            mb: 2,
            fontWeight: 'bold',
            color: '#ff9800',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          Sentry Debug Panel
        </Typography>

        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <Button
            variant="contained"
            color="error"
            size="small"
            onClick={triggerTestError}
            fullWidth
          >
            Trigger Test Error
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={clearLogs}
            sx={{ color: 'white', borderColor: 'white' }}
          >
            Clear
          </Button>
        </Box>

        <Paper
          sx={{
            p: 1,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            maxHeight: 300,
            overflowY: 'auto',
            border: '1px solid rgba(255, 255, 255, 0.2)',
          }}
        >
          {logs.length === 0 ? (
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)' }}>
              No logs yet. Click &quot;Trigger Test Error&quot; to test Sentry.
            </Typography>
          ) : (
            logs.map((log, index) => (
              <Box
                key={index}
                sx={{
                  mb: 1,
                  pb: 1,
                  borderBottom:
                    index < logs.length - 1
                      ? '1px solid rgba(255,255,255,0.1)'
                      : 'none',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    color:
                      log.type === 'error'
                        ? '#f44336'
                        : log.type === 'success'
                          ? '#4caf50'
                          : '#2196f3',
                    fontWeight: 'bold',
                  }}
                >
                  [{log.timestamp}] {log.type.toUpperCase()}
                </Typography>
                <Typography variant="body2" sx={{ color: 'white', mt: 0.5 }}>
                  {log.message}
                </Typography>
                {log.details && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mt: 0.5,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'rgba(255,255,255,0.7)',
                        fontFamily: 'monospace',
                        flex: 1,
                        wordBreak: 'break-all',
                      }}
                    >
                      {log.details}
                    </Typography>
                    {log.eventId && (
                      <IconButton
                        size="small"
                        onClick={() =>
                          copyToClipboard(log.eventId!, log.eventId!)
                        }
                        sx={{
                          color: copiedId === log.eventId ? '#4caf50' : 'white',
                          p: 0.5,
                          '&:hover': {
                            backgroundColor: 'rgba(255,255,255,0.1)',
                          },
                        }}
                        title="Copy Event ID"
                      >
                        {copiedId === log.eventId ? (
                          <span style={{ fontSize: '14px' }}>✓</span>
                        ) : (
                          <span style={{ fontSize: '14px' }}>📋</span>
                        )}
                      </IconButton>
                    )}
                  </Box>
                )}
              </Box>
            ))
          )}
        </Paper>

        <Typography
          variant="caption"
          sx={{
            display: 'block',
            mt: 1,
            color: 'rgba(255,255,255,0.5)',
            textAlign: 'center',
          }}
        >
          Remove ?sentryDebug=true to hide
        </Typography>
      </Paper>
    </Box>
  );
};
